
import { supabase } from '@/integrations/supabase/client';

/**
 * Checks if the current user has admin status using multiple verification methods
 * for consistent role verification across the application
 */
export const checkAdminStatus = async () => {
  try {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('Error fetching session:', sessionError);
      return { isAdmin: false, error: sessionError };
    }
    
    if (!sessionData.session) {
      console.log('No active session found');
      return { isAdmin: false };
    }
    
    // Always log user ID and email for debugging
    console.log(`Checking admin status for user: ${sessionData.session.user.email} (ID: ${sessionData.session.user.id})`);
    
    // PRIMARY METHOD: Check directly with email in client_onboarding
    // This is most reliable because email is consistent across auth.users and client_onboarding
    try {
      console.log('PRIMARY CHECK: Using direct query with email...');
      const { data: onboardingData, error: onboardingError } = await supabase
        .from('client_onboarding')
        .select('role')
        .eq('email', sessionData.session.user.email)
        .maybeSingle();
        
      if (onboardingError) {
        console.error('Error checking admin status via email query:', onboardingError);
        // Continue to other methods
      } else if (onboardingData) {
        const isAdmin = onboardingData.role === 'admin';
        console.log(`Email-based admin check result: ${isAdmin} (User: ${sessionData.session.user.email})`);
        return { isAdmin };
      } else {
        console.log('No onboarding data found for email:', sessionData.session.user.email);
      }
    } catch (emailCheckError) {
      console.error('Error in email-based admin status check:', emailCheckError);
      // Continue to other methods
    }
    
    // SECONDARY METHOD: Try RPC function with user ID
    try {
      console.log('SECONDARY CHECK: Using RPC with user ID...');
      const { data: isAdminByRpc, error: rpcError } = await supabase.rpc('is_admin', { 
        user_id: sessionData.session.user.id 
      });
      
      if (rpcError) {
        console.error('Error checking admin status via RPC:', rpcError);
      } else {
        console.log(`RPC admin check result: ${!!isAdminByRpc} (User: ${sessionData.session.user.email})`);
        return { isAdmin: !!isAdminByRpc };
      }
    } catch (rpcError) {
      console.error('Error in RPC admin status check:', rpcError);
      // Continue to final methods
    }
    
    // TERTIARY METHOD: Try checking with user ID in client_onboarding
    try {
      console.log('TERTIARY CHECK: Direct query with user ID...');
      const { data: idCheckData, error: idCheckError } = await supabase
        .from('client_onboarding')
        .select('role')
        .eq('id', sessionData.session.user.id)
        .maybeSingle();
        
      if (idCheckError) {
        console.error('Error in ID-based admin check:', idCheckError);
      } else if (idCheckData) {
        const isAdminById = idCheckData.role === 'admin';
        console.log(`ID-based admin check result: ${isAdminById} (User: ${sessionData.session.user.email})`);
        return { isAdmin: isAdminById };
      } else {
        console.log('No onboarding data found for ID:', sessionData.session.user.id);
      }
    } catch (idCheckError) {
      console.error('Error in ID-based admin status check:', idCheckError);
    }
    
    // FINAL FALLBACK: Direct database query as last resort
    // Since we can't use a custom RPC that doesn't exist, we'll use a direct query
    try {
      console.log('FINAL FALLBACK: Using email-based admin check via direct query...');
      const { data: manualCheckData, error: manualCheckError } = await supabase
        .from('client_onboarding')
        .select('role')
        .eq('email', sessionData.session.user.email)
        .eq('role', 'admin')
        .maybeSingle();
      
      if (manualCheckError) {
        console.error('Error in manual admin status check:', manualCheckError);
      } else {
        const isAdmin = !!manualCheckData;
        console.log(`Manual admin check result: ${isAdmin} (User: ${sessionData.session.user.email})`);
        return { isAdmin };
      }
    } catch (manualCheckError) {
      console.error('Error in manual admin status check:', manualCheckError);
    }
    
    // If all methods fail, conservatively return false for admin status
    console.log('ALL ADMIN CHECKS FAILED. Defaulting to non-admin status.');
    return { isAdmin: false };
  } catch (error) {
    console.error('Error checking admin status:', error);
    return { isAdmin: false, error };
  }
};
